<template>
  <section class="lab-details" aria-label="Test details">
    <div class="details-head">
      <h3>Test details</h3>
      <span class="sub">As entered on the lab PC for this test</span>
    </div>

    <div class="details-remarks">
      <div class="label">Remarks</div>
      <p :class="{ empty: !remarks }">{{ remarks || 'No remarks entered.' }}</p>
    </div>

    <div class="details-cols">
      <div v-for="group in groups" :key="group.name" class="dt-group">
        <h4>{{ group.name }}</h4>
        <dl>
          <div v-for="row in group.rows" :key="row.label" class="dt-row">
            <dt>{{ row.label }}</dt>
            <dd :class="{ empty: row.value === NONE }">{{ row.value }}</dd>
          </div>
        </dl>
      </div>

      <div class="dt-group">
        <h4>Indoor and outdoor unit</h4>
        <table class="dt-pair">
          <thead>
            <tr><th scope="col"></th><th scope="col">Indoor</th><th scope="col">Outdoor</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in unitRows" :key="row.label">
              <th scope="row">{{ row.label }}</th>
              <td :class="{ empty: row.indoor === NONE }">{{ row.indoor }}</td>
              <td :class="{ empty: row.outdoor === NONE }">{{ row.outdoor }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="dt-group">
        <h4>Test condition</h4>
        <table class="dt-pair">
          <thead>
            <tr><th scope="col"></th><th scope="col">D.B. Temp.</th><th scope="col">W.B. Temp.</th></tr>
          </thead>
          <tbody>
            <tr v-for="row in conditionRows" :key="row.label">
              <th scope="row">{{ row.label }}</th>
              <td :class="{ empty: row.db === NONE }">{{ row.db }}</td>
              <td :class="{ empty: row.wb === NONE }">{{ row.wb }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </section>
</template>

<script>
const NONE = '—'

// A field the lab software leaves unset ("--", blank) reads as a dash, so the
// layout stays the same as the lab's report whatever was filled in
const show = (value) => {
  const text = value === null || value === undefined ? '' : String(value).trim()
  return !text || /^-+$/.test(text) ? NONE : text
}

const withUnit = (value, unit) => {
  const text = show(value)
  return text === NONE ? NONE : `${text} ${unit}`
}

// The calorimeter's rated figures are watts; blank ones stay blank
const watts = (value) => {
  const text = show(value)
  const number = Number(text.replace(/,/g, ''))
  return text !== NONE && Number.isFinite(number) ? `${number.toLocaleString('en-US')} W` : text
}

// Nozzle sizes are diameters ("Ø100, Ø150"). The Ø arrives garbled from the
// lab PC's code page, or stripped, so the sizes are read as numbers.
const nozzles = (value) => {
  const sizes = String(value || '').match(/\d+(?:\.\d+)?/g)
  return sizes ? sizes.map(n => `Ø${n}`).join(', ') : show(value)
}

// Lines 3 to 25 of the lab's Performance Test Report, in its own words and
// grouping, from the test details (the Memo table) the lab agent sends when a
// test starts. The remarks lead, as they are what changes test to test.
export default {
  name: 'LabDetails',
  props: {
    metadata: { type: Object, default: () => ({}) },
    // Live tests have no end time yet
    running: { type: Boolean, default: false }
  },
  data () {
    return { NONE }
  },
  computed: {
    // Some column names carry stray spaces ("ConnectingPipe ")
    m () {
      const out = {}
      Object.entries(this.metadata || {}).forEach(([key, value]) => { out[key.trim()] = value })
      return out
    },
    heating () {
      return /heat/i.test(this.m.TestStyle || '')
    },
    remarks () {
      const text = show(this.m.Memo)
      return text === NONE ? '' : text.replace(/\r\n?/g, '\n').replace(/\n{2,}/g, '\n').trim()
    },
    groups () {
      const m = this.m
      return [
        {
          name: 'Test',
          rows: [
            { label: 'Test Request No.', value: show(m.TestRequestNum) },
            { label: 'Test ID', value: show(m.TestID) },
            { label: 'Report No.', value: show(m.ReportNO) },
            { label: 'Requested by', value: show(m.RequestedBy) },
            { label: 'Format No.', value: show(m.FormatNO) },
            { label: 'Lab name', value: show(m.LabName) },
            { label: 'Test Name', value: show(m.TestName) },
            { label: 'Test mode', value: show(m.TestStyle) },
            { label: 'Start Time', value: show(m.TestTime) },
            { label: 'End Time', value: this.running ? 'Test running' : show(m.EndTime) }
          ]
        },
        {
          name: 'Unit',
          rows: [
            { label: 'Unit Type', value: show(m.UnitType) },
            { label: 'Product Model', value: show(m.ProductModel) },
            { label: 'Refrigerant Charge', value: [m.RefType, m.RefrigerantCharge].map(show).filter(v => v !== NONE).join(' - ') || NONE },
            { label: 'Rated Capacity', value: watts(this.heating ? m.RatedHeatingCapacity : m.RatedCoolingCapacity) },
            { label: 'Rated Power', value: watts(this.heating ? m.RatedHeatingPower : m.RatedCoolingPower) },
            { label: 'Rated Voltage', value: show(m.RatedVoltage) },
            { label: 'Rated Current', value: show(m.RatedCurrent) },
            { label: 'Rated Frequency', value: show(m.RatedFrequency) },
            { label: 'Connecting Pipe', value: show(m.ConnectingPipe) }
          ]
        },
        {
          name: 'Test setup',
          rows: [
            { label: 'Compressor Model', value: show(m.CompressorModel) },
            { label: 'Nozzle Used', value: nozzles(m.AirANozzles) },
            { label: 'Test Standard', value: show(m.TestStandard) },
            { label: 'Exp. Device / Capillary', value: show(m.ExpDevice) },
            { label: 'Fan Speed', value: show(m.FanSpeed) },
            { label: 'Test Purpose', value: show(m.TestPurpose) },
            { label: 'Static Pressure', value: withUnit(m.IndoorStaticPressure, 'Pa') }
          ]
        }
      ]
    },
    unitRows () {
      const m = this.m
      const pair = (label, indoor, outdoor) => ({ label, indoor: show(m[indoor]), outdoor: show(m[outdoor]) })
      return [
        pair('Model No.', 'IndoorModelNO', 'OutdoorModelNO'),
        pair('Serial No.', 'IndoorSerialNO', 'OutdoorSerialNO'),
        pair('Nos. of unit', 'IndoorNosofUnit', 'OutdoorNosofUnit'),
        pair('Fan / Blower', 'IndoorFan', 'OutdoorFan'),
        pair('Motor model', 'IndoorModel', 'OutdoorModel'),
        pair('Motor spec', 'IndoorSpeed', 'OutdoorSpeed'),
        pair('Coil size / Fin length', 'IndoorCoilsize', 'OutdoorCoilsize'),
        pair('Row/FPI', 'IndoorRow', 'OutdoorRow'),
        pair('Tube Dia.', 'IndoorTubeDia', 'OutdoorTubeDia'),
        pair('Nos. Circuit', 'IndoorNosCircuit', 'OutdoorNosCircuit')
      ]
    },
    conditionRows () {
      const m = this.m
      return [
        { label: 'Indoor', db: withUnit(m.IndoorEnteringDB, '°C'), wb: withUnit(m.IndoorEnteringWB, '°C') },
        { label: 'Outdoor', db: withUnit(m.OutdoorEnteringDB, '°C'), wb: withUnit(m.OutdoorEnteringWB, '°C') }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.lab-details {
  flex: 1 0 auto;
  padding: 12px 20px 20px;
}

.details-head {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 12px;

  h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: var(--lab-accent);
  }

  .sub {
    font-size: 12px;
    color: var(--lab-muted);
  }
}

.details-remarks {
  margin-bottom: 12px;
  padding: 10px 14px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;

  .label {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #92400e;
    margin-bottom: 4px;
  }

  p {
    margin: 0;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-line;
    color: var(--lab-text);

    &.empty { color: var(--lab-muted); }
  }
}

// Cards flow down and across like the Readings view; a phone gets one column
.details-cols {
  // Wide enough that the indoor/outdoor table keeps model numbers on one line
  column-width: 380px;
  column-gap: 12px;
}

.dt-group {
  break-inside: avoid;
  margin-bottom: 12px;
  background: var(--lab-surface);
  border: 1px solid var(--lab-border);
  border-radius: 6px;
  overflow: hidden;

  h4 {
    margin: 0;
    padding: 7px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--lab-accent);
    background: var(--lab-sidebar);
    border-bottom: 1px solid var(--lab-border);
  }

  dl { margin: 0; }
}

.dt-row {
  display: grid;
  grid-template-columns: minmax(120px, 42%) 1fr;
  gap: 10px;
  padding: 5px 10px;
  font-size: 12px;
  border-bottom: 1px solid #f0f1f3;

  &:last-child { border-bottom: none; }

  dt { color: var(--lab-muted); }

  dd {
    margin: 0;
    font-weight: 600;
    overflow-wrap: anywhere;

    &.empty { color: #b8bec8; font-weight: 400; }
  }
}

.dt-pair {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  th,
  td {
    padding: 5px 10px;
    text-align: left;
    border-bottom: 1px solid #f0f1f3;
    vertical-align: top;
    overflow-wrap: anywhere;
  }

  thead th {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--lab-muted);
  }

  tbody th {
    width: 34%;
    font-weight: 400;
    color: var(--lab-muted);
  }

  td {
    font-weight: 600;
    font-variant-numeric: tabular-nums;

    &.empty { color: #b8bec8; font-weight: 400; }
  }

  tbody tr:last-child th,
  tbody tr:last-child td { border-bottom: none; }
}

@media (max-width: 640px) {
  .lab-details { padding: 10px 12px 12px; }

  .dt-row,
  .dt-pair th,
  .dt-pair td {
    padding: 7px 12px;
    font-size: 13px;
  }
}
</style>
